import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ActivityIndicator } from 'react-native';
import { MARKETPLACE_COLORS } from '@/constants/residents/marketplaceColors';
import { PRIMITIVE_COLORS, SEMANTIC_COLORS } from '@/constants/residents/residentColors';

interface ChatCTACardProps {
  onPress?: () => void;
  variant?: 'mobile' | 'desktop';
  loading?: boolean;
}

// Chat Icon Component
const ChatIcon: React.FC<{ variant: 'mobile' | 'desktop' }> = ({ variant }) => (
  <View style={styles.chatIcon}>
    <View style={[
      styles.chatBubble,
      { borderColor: variant === 'mobile' ? MARKETPLACE_COLORS.black : PRIMITIVE_COLORS.gray950 }
    ]} />
  </View>
);

/**
 * ChatCTACard - Call-to-action card for starting a chat
 * Used in both mobile and desktop detail views
 */
export const ChatCTACard: React.FC<ChatCTACardProps> = ({ 
  onPress, 
  variant = 'mobile',
  loading = false,
}) => (
  <View style={variant === 'mobile' ? styles.mobileCard : styles.desktopCard}>
    <View style={styles.textContainer}>
      <Text style={variant === 'mobile' ? styles.mobileCTATitle : styles.desktopCTATitle}>
        You've got questions?
      </Text>
      <Text style={variant === 'mobile' ? styles.mobileCTASubtitle : styles.desktopCTASubtitle}>
        Chat with the community
      </Text>
    </View>
    <TouchableOpacity
      style={variant === 'mobile' ? styles.mobileButton : styles.desktopButton}
      onPress={onPress}
      activeOpacity={0.8}
      disabled={loading}
    >
      {loading ? (
        <ActivityIndicator 
          size="small" 
          color={variant === 'mobile' ? MARKETPLACE_COLORS.black : PRIMITIVE_COLORS.gray950} 
        />
      ) : (
        <>
          <ChatIcon variant={variant} />
          <Text style={variant === 'mobile' ? styles.mobileButtonText : styles.desktopButtonText}>
            Start chat
          </Text>
        </>
      )}
    </TouchableOpacity>
  </View>
);

const styles = StyleSheet.create({
  // Shared styles
  textContainer: {
    flex: 1,
    gap: 2,
  },
  chatIcon: {
    width: 18,
    height: 18,
    alignItems: 'center',
    justifyContent: 'center',
  },
  chatBubble: {
    width: 14,
    height: 12,
    borderWidth: 1.5,
    borderRadius: 6,
    borderBottomLeftRadius: 2,
  },
  
  // Mobile styles
  mobileCard: {
    backgroundColor: MARKETPLACE_COLORS.uploadAreaBackground,
    borderRadius: 16,
    padding: 20,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  mobileCTATitle: {
    fontSize: 14,
    fontWeight: '500',
    color: MARKETPLACE_COLORS.textPrimary,
    lineHeight: 21,
  },
  mobileCTASubtitle: {
    fontSize: 12,
    fontWeight: '300',
    color: MARKETPLACE_COLORS.textSecondary,
    lineHeight: 18,
  },
  mobileButton: {
    backgroundColor: MARKETPLACE_COLORS.accentRed,
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
    height: 34,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  mobileButtonText: {
    fontSize: 12,
    fontWeight: '500',
    color: MARKETPLACE_COLORS.black,
    lineHeight: 18,
  },

  // Desktop styles
  desktopCard: {
    marginHorizontal: 24,
    backgroundColor: PRIMITIVE_COLORS.gray850,
    borderRadius: 16,
    padding: 20,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  desktopCTATitle: {
    fontSize: 14,
    fontWeight: '500',
    color: SEMANTIC_COLORS.text.primary,
    lineHeight: 21,
  },
  desktopCTASubtitle: {
    fontSize: 12,
    fontWeight: '300',
    color: PRIMITIVE_COLORS.gray300,
    lineHeight: 18,
  },
  desktopButton: {
    backgroundColor: PRIMITIVE_COLORS.resioPink,
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
    height: 34,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  desktopButtonText: {
    fontSize: 12,
    fontWeight: '500',
    color: PRIMITIVE_COLORS.gray950,
    lineHeight: 18,
  },
});
